import _ from "lodash";
import { useSnackbar } from "notistack";
import Pusher, { Channel } from "pusher-js";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState
} from "react";
import { useHistory } from "react-router-dom";

import { Button } from "@material-ui/core";

import { useAuth0 } from "src/components/AuthProvider";
import { useCaptcha } from "src/components/CaptchaProvider";
import { useTenant } from "src/components/LoggedInAdminProvider";
import { useSubaccount } from "src/components/SubaccountProvider";
import { getCampaign_campaign } from "src/graphql/generated/getCampaign";

import getAdminApiBaseUrl from "../../util/getAdminApiBaseUrl";

type ChannelMemberInfo = { full_name: string };
type ChannelMember = { id: string; info: ChannelMemberInfo };

export type EditEvent = { section: string };
export const CampaignEditorsContext = createContext({
  sendEditEvent: _.noop
});

export function useCampaignEditEvents(): () => void {
  return useContext(CampaignEditorsContext).sendEditEvent;
}

export default function CampaignEditorsProvider({
  children,
  campaign,
  onRefetch,
  activeSection
}: {
  children: ReactNode;
  campaign: getCampaign_campaign;
  onRefetch: () => Promise<any>;
  activeSection: string;
}): JSX.Element {
  const tenant = useTenant()!;
  const { subaccountLink } = useSubaccount()!;
  const auth0 = useAuth0();

  const [pusher, setPusher] = useState<Pusher | null>(null);
  const [channel, setChannel] = useState<Channel | null>(null);

  const [me, setMe] = useState<ChannelMember | null>(null);
  const [otherMembers, setOtherMembers] = useState<ChannelMember[]>([]);

  const snackbar = useSnackbar();
  const history = useHistory();

  const [refetchingCampaign, setRefetchingCamapign] = useState(false);

  const refreshAndGoToSection = useCallback(
    async (section: string) => {
      setRefetchingCamapign(true);
      await onRefetch();
      setRefetchingCamapign(false);
      snackbar.closeSnackbar();
      history.push(subaccountLink(`/campaigns/${campaign.id}/${section}`));
    },
    [
      setRefetchingCamapign,
      onRefetch,
      snackbar,
      history,
      subaccountLink,
      campaign
    ]
  );

  const handleCampaignEdited = useCallback(
    (event: EditEvent, metadata: { user_id: string }) => {
      if (metadata.user_id === me?.id) {
        return;
      }
      const editedByFullname = otherMembers.find(
        user => user.id === metadata.user_id
      )?.info.full_name;
      const message =
        ({
          basics: "This campaign's basics were edited",
          integration: "This campaign's integration was edited",
          questions: "This campaign's questions were edited",
          contacts: "This campaign's contacts were updated",
          "caller-id": "This campaign's caller ID was edited",
          voicemail: "This campaign's voicemail settings were edited",
          passes: "This campaign's number of passes was updated",
          start: "This campaign was started"
        }[event.section] || "This campaign was edited") +
        " by " +
        (editedByFullname || "another user") +
        ".";
      snackbar.enqueueSnackbar(message, {
        variant: event.section === activeSection ? "error" : "warning",
        action: key => [
          event.section === activeSection ? (
            <Button onClick={() => window.location.reload()}>Refresh</Button>
          ) : (
            <Button
              onClick={() => refreshAndGoToSection(event.section)}
              disabled={refetchingCampaign}
            >
              View Changes
            </Button>
          ),
          <Button onClick={() => snackbar.closeSnackbar(key)}>Dismiss</Button>
        ],
        persist: true
      });
    },
    [
      me,
      otherMembers,
      snackbar,
      activeSection,
      refreshAndGoToSection,
      refetchingCampaign
    ]
  );

  const disconnectFromPresenceChannel = useCallback(() => {
    if (pusher) {
      pusher.disconnect();
      setPusher(null);
    }
  }, [pusher, setPusher]);

  const captcha = useCaptcha();
  const connectToPresenceChannel = useCallback(async () => {
    if (campaign.pusherConfigs.length > 0) {
      const { appId, key, cluster } = campaign.pusherConfigs[0];

      const token = await auth0.getAccessTokenSilently();
      const newPusher = new Pusher(key, {
        cluster: cluster,
        forceTLS: true,
        channelAuthorization: {
          endpoint: `${await getAdminApiBaseUrl()}/admin/pusher-auth?app_id=${appId}`,
          transport: "ajax",
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
            "STW-Dialer-Tenant": tenant.slug,
            ...captcha.getLatestTokenHeaders()
          }
        }
      });
      setPusher(newPusher);

      const newChannel = newPusher.subscribe(
        `presence-campaignEdit-${tenant.slug}-${campaign.id}`
      );
      newChannel.bind(
        "pusher:subscription_succeeded",
        (data: {
          members: { [id: string]: ChannelMemberInfo };
          me: ChannelMember;
        }) => {
          setMe(data.me);
          setOtherMembers(
            Object.keys(data.members)
              .filter(memberId => memberId !== data.me.id)
              .map(id => ({ id, info: data.members[id] }))
          );
        }
      );
      newChannel.bind("pusher:subscription_error", (data: any) => {
        console.log("Pusher subscription failed", data);
        if (data.status === 401) {
          setPusher(null);
        }

        if (data.status === 405) {
          console.log("WAF challenge detected; showing captcha");
          captcha.showCaptcha(() => {
            disconnectFromPresenceChannel();
            void connectToPresenceChannel();
          });
        }
      });
      newChannel.bind("pusher:member_added", (member: ChannelMember) => {
        if (member.id !== me?.id) {
          setOtherMembers(otherMembers => [...otherMembers, member]);
        }
      });
      newChannel.bind("pusher:member_removed", (member: ChannelMember) => {
        setOtherMembers(otherEditingUsers =>
          otherEditingUsers.filter(user => user.id !== member.id)
        );
      });
      setChannel(newChannel);
    }
  }, [
    setPusher,
    setChannel,
    campaign,
    tenant,
    auth0,
    me,
    captcha,
    disconnectFromPresenceChannel
  ]);

  useEffect(() => {
    if (channel) {
      // we need to re-register the campaign edited handler when it changes
      channel.unbind("client-campaignedit");
      channel.bind("client-campaignedit", handleCampaignEdited);
    }
  }, [channel, handleCampaignEdited]);

  useEffect(() => {
    if (campaign.pusherConfigs.length > 0) {
      const pusherKey = campaign.pusherConfigs[0].key;
      if (pusher && pusher.key === pusherKey) {
        // do nothing
      } else if (pusher && pusher.key !== pusherKey) {
        disconnectFromPresenceChannel();
        void connectToPresenceChannel();
      } else {
        void connectToPresenceChannel();
      }
    }
  }, [
    campaign,
    pusher,
    connectToPresenceChannel,
    disconnectFromPresenceChannel
  ]);
  useEffect(
    () => () => disconnectFromPresenceChannel(),
    [disconnectFromPresenceChannel]
  );

  const sendEditEvent = useCallback(() => {
    if (channel) {
      channel.trigger("client-campaignedit", {
        section: activeSection
      });
    }
  }, [channel, activeSection]);

  return (
    <CampaignEditorsContext.Provider value={{ sendEditEvent }}>
      {children}
    </CampaignEditorsContext.Provider>
  );
}
